import { z } from "zod";
import { CryptoMarket } from "../api/crypto-api";
import { GlowIdTypes } from "../schema/GlowIdSchema";
import { NftCollection, NftInfoZ } from "../schema/SolanaSchema";
import { TokenProgram } from "../schema/SolanaTokenProgram";
import { Solana } from "../solana/solana-base";
import { Base58Z, TStringZ } from "./data-types";
import { SolanaTransaction } from "./solana-transaction";

export namespace SolanaTransactionInfo {
  export enum IconName {
    AppInteraction = "app_interaction",
    Bid = "bid",
    Burn = "burn",
    BuyNft = "buy_nft",
    CancelBid = "cancel_bid",
    Diamond = "diamond",
    Dollar = "dollar",
    Failed = "failed",
    FormFunction = "formfn",
    Fractal = "fractal",
    Generic = "generic",
    Hyperspace = "hyperspace",
    Info = "info",
    Invite = "invite",
    ListNft = "list_nft",
    MagicEden = "magic_eden",
    Marketplace = "marketplace",
    MarketplaceDeposit = "marketplace_deposit",
    MarketplaceWithdraw = "marketplace_withdraw",
    Mint = "mint",
    OpenSea = "opensea",
    Receive = "receive",
    Referral = "referral",
    Register = "register",
    Send = "send",
    Solanart = "solanart",
    SoldNft = "sold_nft",
    Stake = "stake",
    StakeWithdraw = "stake_withdraw",
    SubaccountCleanedUp = "subaccount_cleaned_up",
    SubaccountSetUp = "subaccount_set_up",
    Swap = "swap",
    Tag = "tag",
    Tick = "tick",
    UnlistNft = "unlist_nft",
    Unstake = "unstake",
    Updated = "updated",
    Wallet = "wallet",
    Yawww = "yawww",
  }

  const IconNameZ = z.nativeEnum(IconName);

  export const MarketplaceToIcon: Record<Solana.Marketplace, IconName> = {
    [Solana.Marketplace.MagicEden]: IconName.MagicEden,
    [Solana.Marketplace.Solanart]: IconName.Solanart,
    [Solana.Marketplace.OpenSea]: IconName.OpenSea,
    [Solana.Marketplace.FormFunction]: IconName.FormFunction,
    [Solana.Marketplace.Fractal]: IconName.Fractal,
    [Solana.Marketplace.Hyperspace]: IconName.Hyperspace,
    [Solana.Marketplace.Yawww]: IconName.Yawww,
  };

  export const IconZ = z.union([
    z.object({
      type: z.literal("solana"),
    }),
    z.object({
      type: z.literal("image"),
      image: z.string(),
    }),
    z.object({
      type: z.literal("coin"),
      coin: TokenProgram.CoinInfoZ,
    }),
    z.object({
      type: z.literal("glow_id"),
      info: GlowIdTypes.BasicInfoZ,
    }),
    z.object({
      type: z.literal("nft"),
      nft: NftInfoZ,
      marketplace: Solana.MarketplaceZ.nullable().optional(),
    }),
    z.object({
      type: z.literal("nft_marketplace"),
      marketplace: Solana.MarketplaceZ,
    }),
    z.object({
      type: z.literal("nft_collection"),
      collection: NftCollection.BasicInfoZ,
    }),
    z.object({
      type: z.literal("swap"),
      coin_sent: SolanaTransaction.SwappedCoinZ,
      coin_received: SolanaTransaction.SwappedCoinZ,
    }),
    z.object({
      type: z.literal("tokens"),
      token_a: z.union([TokenProgram.CoinInfoZ, NftInfoZ]),
      token_b: z.union([TokenProgram.CoinInfoZ, NftInfoZ]),
    }),
    z.object({
      type: z.literal("default"),
    }),
  ]);

  export type Icon = z.infer<typeof IconZ>;

  const TransactionValueModeZ = z.union([
    /** The value is positive and displayed with primary (black) text. */
    z.literal("amount"),
    /** The value is positive and displayed with green text. */
    z.literal("positive"),
    /** The value is negative (abs() * -1) and displayed with red text. */
    z.literal("negative"),
  ]);

  export type TransactionValueMode = z.infer<typeof TransactionValueModeZ>;

  export const TransactionValueZ = z.union([
    z.object({
      mode: TransactionValueModeZ,
      type: z.literal("coin"),
      coin: TokenProgram.CoinInfoZ,
      amount: Solana.TokenAmountZ,
    }),
    z.object({
      mode: TransactionValueModeZ,
      type: z.literal("solana"),
      amount: Solana.SolAmountZ,
    }),
  ]);

  export type TransactionValue = z.infer<typeof TransactionValueZ>;

  export const TransactionSubValueZ = TStringZ.nullable();

  export type TransactionSubValue = z.infer<typeof TransactionSubValueZ>;

  const TransactionDetailSubtitleZ = z.union([
    z.object({
      type: z.literal("text"),
      text: z.string(),
    }),
    z.object({
      type: z.literal("sol-amount"),
      amount: Solana.SolAmountZ,
    }),
    z.object({
      type: z.literal("coin-amount"),
      amount: Solana.TokenAmountZ,
      coin: TokenProgram.CoinInfoZ,
    }),
    z.object({
      type: z.literal("swap"),
      coin_sent: SolanaTransaction.SwappedCoinZ,
      coin_received: SolanaTransaction.SwappedCoinZ,
    }),
  ]);

  export type TransactionDetailSubtitle = z.infer<
    typeof TransactionDetailSubtitleZ
  >;

  export const TextColorZ = z.enum(["primary", "green", "red"]);

  export type TextColor = z.infer<typeof TextColorZ>;

  const TitleDescriptionZ = z.object({
    title: TStringZ,
    title_color: TextColorZ.nullable().optional(), // Default: "primary"
    title_placement: z.enum(["top", "bottom"]).optional(), // Default: "top"
    description: TStringZ.nullable().optional(),
  });

  export type TitleDescription = z.infer<typeof TitleDescriptionZ>;

  const TransactionDetailBlockZ = z.union([
    z.object({
      type: z.literal("nft"),
      nft: NftInfoZ,
      right: TitleDescriptionZ.nullable().optional(),
      suppress_click: z.boolean().nullable().optional(),
    }),
    z.object({
      type: z.literal("sol"),
      amount: Solana.SolAmountZ,
      mode: TransactionValueModeZ,
      subtitle: TStringZ.optional(),
    }),
    z.object({
      type: z.literal("coin"),
      coin: TokenProgram.CoinInfoZ,
      amount: Solana.TokenAmountZ,
      mode: TransactionValueModeZ,
    }),
    z.object({
      type: z.literal("divider"),
    }),
    z.object({
      type: z.literal("chevron"),
    }),
    z.object({
      type: z.literal("address"),
      address_eyebrow: TStringZ.optional(),
      address: Solana.AddressZ,
      right: TitleDescriptionZ.optional(),
    }),
    z.object({
      type: z.literal("simple"),
      icon: IconNameZ,
      title: TStringZ,
      right: z.union([
        z.object({ type: z.literal("address"), address: Solana.AddressZ }),
        z.object({
          type: z.literal("proceeds"),
          proceeds: Solana.SolAmountZ,
          total_amount: Solana.SolAmountZ,
        }),
        z.object({ type: z.literal("text"), text: z.string() }),
      ]),
    }),
    z.object({
      type: z.literal("text"),
      icon: IconNameZ,
      text: TStringZ,
    }),
    z.object({
      type: z.literal("generic"),
      icon: IconZ,
      left: TitleDescriptionZ,
      right: TitleDescriptionZ.nullable().optional(),
    }),
  ]);

  export type TransactionDetailBlock = z.infer<typeof TransactionDetailBlockZ>;

  const TransactionInfoZ = z.object({
    api_id: z.string(), // Used for pagination
    signature: Base58Z,
    timestamp: z.string(),
    category: z.object({
      icon: IconNameZ,
      title: z.string(),
    }),
    icon: IconZ,
    title: z.string(),
    subtitle: z.string(),
    detail_subtitle: TransactionDetailSubtitleZ,
    detail_blocks: z.array(TransactionDetailBlockZ),
    value: TransactionValueZ.nullable(),
    sub_value: TransactionSubValueZ.nullable(),
    sol_exchange_rate: CryptoMarket.ExchangeRateZ,
    token_to_exchange_rate: z.record(CryptoMarket.ExchangeRateZ.nullable()),
    fee: Solana.SolAmountZ.nullable(),
    note: z.string().nullable(),
    is_viewer_involved: z.boolean(),
  });

  export type TransactionInfo = z.infer<typeof TransactionInfoZ>;
}
